// Core game types shared between the Durable Object simulation and the browser client.

export const TICK_RATE = 20;
export const TICK_MS = 1000 / TICK_RATE;
export const TICK_DT = 1 / TICK_RATE;
export const TILE = 16;
export const MAX_PLAYERS = 8;
/** Entities further than this many tiles from a player are not sent to them. */
export const VIEW_RADIUS = 24;
export const PLAYER_RADIUS = 0.32;
export const BASE_MOVE_SPEED = 5.2; // tiles per second
export const MAX_LEVEL = 50;
export const BAG_SIZE = 32;
export const STASH_SIZE = 32;
export const STAT_POINTS_PER_LEVEL = 4;
export const SKILL_POINTS_PER_LEVEL = 1;

export type Element = 'phys' | 'fire' | 'cold' | 'light' | 'poison' | 'magic';

export type ClassId = 'reaver' | 'spellweaver' | 'gunner' | 'cleric' | 'ironclad' | 'hexer';

export type SlotId = 'weapon' | 'offhand' | 'helm' | 'armor' | 'gloves' | 'boots' | 'ring1' | 'ring2' | 'amulet';
export const SLOT_IDS: SlotId[] = ['weapon', 'offhand', 'helm', 'armor', 'gloves', 'boots', 'ring1', 'ring2', 'amulet'];

export type Rarity = 0 | 1 | 2 | 3; // common, magic, rare, legendary

export type StatKey =
  | 'str' | 'dex' | 'vit' | 'ene'
  | 'hp' | 'mp' | 'hpRegen' | 'mpRegen'
  | 'armor' | 'armorPct'
  | 'minDmg' | 'maxDmg' | 'dmgPct'
  | 'fireDmg' | 'coldDmg' | 'lightDmg' | 'poisonDmg'
  | 'atkSpd' | 'crit' | 'critDmg' | 'moveSpd'
  | 'lifeSteal' | 'manaSteal'
  | 'resFire' | 'resCold' | 'resLight' | 'resPoison' | 'resAll'
  | 'magicFind' | 'goldFind' | 'allSkills' | 'thorns' | 'dmgReduce' | 'cdr';

export type Mods = Partial<Record<StatKey, number>>;

export interface Item {
  uid: string;
  base: string; // key into ITEM_BASES
  name: string;
  rarity: Rarity;
  ilvl: number;
  req: number; // required level
  slot: SlotId | 'ring';
  icon: string;
  /** Weapon damage (weapons only). */
  dmg?: [number, number];
  /** Base armor (armor pieces only). */
  armor?: number;
  mods: Mods;
  /** Sell value in gold. */
  value: number;
  /** Flavour text (legendaries). */
  flavor?: string;
  /** Legendary proc key. */
  proc?: string;
}

export interface HeroSave {
  id: string;
  name: string;
  cls: ClassId;
  level: number;
  xp: number;
  stats: { str: number; dex: number; vit: number; ene: number };
  statPts: number;
  skillPts: number;
  skills: Record<string, number>;
  /** Skill ids bound to [LMB, RMB, 1, 2, 3, 4]. */
  binds: (string | null)[];
  equip: Partial<Record<SlotId, Item>>;
  bag: (Item | null)[];
  stash: (Item | null)[];
  gold: number;
  hpPots: number;
  mpPots: number;
  /** Highest realm index unlocked. */
  progress: number;
  /** Boss ids this hero has already defeated (for one-time skill point bonus). */
  bosses: string[];
  deaths: number;
  kills: number;
  created: number;
  updated: number;
}

export interface Derived {
  str: number;
  dex: number;
  vit: number;
  ene: number;
  maxHp: number;
  maxMp: number;
  hpRegen: number;
  mpRegen: number;
  armor: number;
  minDmg: number;
  maxDmg: number;
  dmgPct: number;
  fireDmg: number;
  coldDmg: number;
  lightDmg: number;
  poisonDmg: number;
  atkSpd: number; // percent
  crit: number; // percent
  critDmg: number; // percent bonus over 100
  moveSpeed: number; // tiles per sec
  lifeSteal: number;
  manaSteal: number;
  resFire: number;
  resCold: number;
  resLight: number;
  resPoison: number;
  magicFind: number;
  goldFind: number;
  allSkills: number;
  thorns: number;
  dmgReduce: number;
  cdr: number;
}

/** A value that scales with skill rank: base + perRank * (rank - 1). */
export type Rank = number | [number, number];

export function rv(r: Rank | undefined, rank: number): number {
  if (r === undefined) return 0;
  if (typeof r === 'number') return r;
  return r[0] + r[1] * Math.max(0, rank - 1);
}

export type StatusId = 'burn' | 'chill' | 'freeze' | 'poison' | 'stun' | 'shock' | 'bleed' | 'weaken' | 'fear' | 'slow';

export const STATUS_BITS: Record<StatusId, number> = {
  burn: 1,
  chill: 2,
  freeze: 4,
  poison: 8,
  stun: 16,
  shock: 32,
  bleed: 64,
  weaken: 128,
  fear: 256,
  slow: 2,
};

/** Bits for positive buffs, shown on entities as overlays. */
export const BUFF_BITS = {
  shield: 512,
  rage: 1024,
  invuln: 2048,
  bless: 4096,
  /** Downed / awaiting revive. */
  downed: 8192,
};

export interface StatusApply {
  id: StatusId;
  dur: Rank;
  /** Damage over time, as a fraction of the triggering hit, per second. */
  dot?: Rank;
  chance?: number;
}

export type ProjVisual =
  | 'firebolt' | 'icebolt' | 'spark' | 'bone' | 'poison' | 'arcane' | 'bullet' | 'pellet' | 'laser'
  | 'rocket' | 'grenade' | 'holy' | 'hammer' | 'arrow' | 'wave' | 'spit' | 'shard' | 'fireball' | 'void' | 'plasma';

export interface ExplodeDef {
  r: number;
  mult: Rank;
  el: Element;
  vis: string;
  status?: StatusApply;
  knock?: number;
}

export interface ProjDef {
  vis: ProjVisual;
  speed: number;
  life: number;
  r: number;
  mult: Rank;
  el: Element;
  count?: Rank;
  spread?: number; // total degrees
  pierce?: Rank;
  status?: StatusApply;
  knock?: number;
  explode?: ExplodeDef;
  homing?: number; // radians/second
  /** Lobbed: flies over everything and detonates at the aimed point. */
  lob?: boolean;
  /** Heals allies it touches for this % of their max HP (Holy Bolt). */
  heals?: Rank;
  bounce?: number;
  /** Ignores walls. */
  ghost?: boolean;
  /** Spiral outward from the caster (Divine Hammer). */
  spiral?: boolean;
  /** Fired in a ring around the caster rather than aimed. */
  ring?: boolean;
}

export type EffectDef =
  | { k: 'arc'; range: number; angle: number; mult: Rank; el: Element; status?: StatusApply; knock?: number; vis?: string; execute?: number }
  | { k: 'proj'; proj: ProjDef }
  | { k: 'nova'; r: Rank; mult: Rank; el: Element; at: 'self' | 'target'; delay?: number; status?: StatusApply; knock?: number; pull?: number; vis: string; heal?: Rank }
  | { k: 'ground'; r: Rank; dur: Rank; every: number; mult: Rank; el: Element; at: 'self' | 'target'; status?: StatusApply; heal?: Rank; pull?: number; vis: string }
  | { k: 'chain'; range: number; jumps: Rank; mult: Rank; el: Element; falloff: number; status?: StatusApply; vis: string }
  | { k: 'beam'; range: number; mult: Rank; el: Element; steal?: Rank; status?: StatusApply; vis: string }
  | { k: 'heal'; pct: Rank; r: number; vis: string; flat?: Rank }
  | { k: 'buff'; buff: string; r: number; dur: Rank; mods: Partial<Record<StatKey, Rank>>; bit?: number; invuln?: boolean; unkillable?: boolean; vis?: string }
  | { k: 'debuff'; r: Rank; at: 'self' | 'target'; status: StatusApply; vis: string; dmgTaken?: Rank }
  | { k: 'summon'; unit: string; count: Rank; max: Rank; dur?: Rank; vis?: string }
  | { k: 'dash'; speed: number; maxDist: Rank; mult: Rank; el: Element; stun?: number; knock?: number; vis?: string }
  | { k: 'leap'; maxDist: Rank; r: number; mult: Rank; el: Element; status?: StatusApply; vis: string }
  | { k: 'blink'; maxDist: Rank; vis?: string }
  | { k: 'taunt'; r: Rank; dur: Rank; vis?: string }
  | { k: 'revive'; r: number; pct: Rank; vis?: string }
  | { k: 'mine'; r: number; trigger: number; life: Rank; mult: Rank; el: Element; max: Rank; vis: string; status?: StatusApply }
  | { k: 'corpse'; r: number; blast: number; mult: Rank; el: Element; maxCorpses: Rank; vis: string }
  | { k: 'whirl'; dur: Rank; r: number; every: number; mult: Rank; el: Element; speed: number; vis: string }
  | { k: 'summonMinions'; monsters: string[]; count: number; vis?: string };

export type SkillScale = 'melee' | 'ranged' | 'spell' | 'none';

export interface SkillDef {
  id: string;
  cls: ClassId;
  name: string;
  desc: string;
  /** Level required to unlock. */
  tier: number;
  maxRank: number;
  icon: string;
  mana: Rank;
  cd: Rank;
  /** Windup in seconds before effects fire. */
  cast: number;
  /** Recovery after the effect fires, before the next action. */
  recover: number;
  scale: SkillScale;
  target: 'point' | 'self' | 'ally' | 'enemy';
  /** Distance the client will walk into before firing (melee & touch skills). */
  reach?: number;
  anim: 'swing' | 'cast' | 'shoot' | 'slam';
  sfx: string;
  effects: EffectDef[];
  /** Lines shown on the tooltip for current rank. */
  info: (rank: number) => string[];
}

export interface ClassDef {
  id: ClassId;
  name: string;
  title: string;
  desc: string;
  role: string;
  stats: { str: number; dex: number; vit: number; ene: number };
  baseHp: number;
  hpPerLevel: number;
  hpPerVit: number;
  baseMp: number;
  mpPerLevel: number;
  mpPerEne: number;
  look: HeroLook;
  skills: string[];
}

export interface HeroLook {
  skin: string;
  hair: string;
  armor: string;
  trim: string;
  /** Sprite variant, see client/sprites. */
  body: 'plate' | 'robe' | 'coat' | 'heavy' | 'cloak';
  hat: 'none' | 'hood' | 'helm' | 'wizard' | 'visor' | 'halo' | 'horns';
  weapon: 'sword' | 'staff' | 'gun' | 'mace' | 'hammer' | 'scythe';
  /** Worn equipment, when known. See shared/look. */
  gear?: import('./look').GearLook;
}

export type Tier = 0 | 1 | 2 | 3; // normal, champion, unique, boss

export type MonsterAI = 'melee' | 'ranged' | 'kamikaze' | 'summoner' | 'turret' | 'skirmish' | 'boss';

export interface MonsterAbility {
  /** Effects fired when the ability resolves. */
  effects: EffectDef[];
  /** Used when target within this distance. */
  range: number;
  minRange?: number;
  cd: number;
  /** Windup seconds (telegraphs on the client). */
  cast: number;
  recover: number;
  weight: number;
  anim: 'swing' | 'cast' | 'shoot' | 'slam';
  sfx: string;
  /** Boss HP% threshold below which this becomes available. */
  belowHp?: number;
  name?: string;
}

export interface MonsterDef {
  id: string;
  name: string;
  sprite: string;
  /** Palette overrides for the sprite template. */
  pal?: Record<string, string>;
  radius: number;
  hp: number;
  dmg: number;
  armor: number;
  speed: number;
  xp: number;
  ai: MonsterAI;
  aggro: number;
  res?: Partial<Record<Element, number>>;
  abilities: MonsterAbility[];
  /** Flying monsters ignore liquids & chasms. */
  fly?: boolean;
  /** Boss-only summon at HP thresholds. */
  phases?: { below: number; monsters: string[]; count: number; say: string }[];
  /** Blows up on death. */
  deathBurst?: ExplodeDef;
  /** Doesn't leave a corpse. */
  noCorpse?: boolean;
  say?: string;
  /** Immobile. */
  rooted?: boolean;
  /** Spawned in a tight pack of this many. */
  pack?: [number, number];
  /** Rendered scale factor. */
  big?: boolean;
}

export type TileType = 0 | 1 | 2 | 3 | 4;
export const T_VOID: TileType = 0; // blocks walking, not projectiles (chasm)
export const T_FLOOR: TileType = 1;
export const T_WALL: TileType = 2; // blocks walking and projectiles
export const T_LIQUID: TileType = 3; // blocks walking, not projectiles
export const T_BLOCK: TileType = 4; // tree / pillar: blocks walking & projectiles, drawn as prop

export interface MapPack {
  x: number;
  y: number;
  /** Elite pack? 0 normal, 1 champion, 2 unique. */
  tier: Tier;
  size: number;
}

export type MapObjectType = 'stairsDown' | 'stairsUp' | 'portal' | 'chest' | 'shrine' | 'gate' | 'npc' | 'stash' | 'bossGate';

export interface MapObject {
  type: MapObjectType;
  x: number;
  y: number;
  /** Zone id for stairs/portals. */
  to?: string;
  /** Extra data, e.g. NPC role or shrine kind. */
  sub?: string;
}

export interface ZoneMap {
  id: string;
  w: number;
  h: number;
  tiles: Uint8Array;
  /** Floor variant / decoration seed per tile. */
  spawn: { x: number; y: number };
  objects: MapObject[];
  packs: MapPack[];
  bossAt?: { x: number; y: number };
  /** Hash of the tile data, compared between server & client to detect desync. */
  hash: number;
}
